import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Slider from "@mui/material/Slider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Rating from "@mui/material/Rating";
import Pagination from "@mui/material/Pagination";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import ImageWithFallback from "@/components/ImageWithFallback";
import LoadingScreen from "@/components/LoadingScreen";
import { useTheme } from "@mui/material/styles";

// Custom debounce hook - in case the imported one has issues

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Filter and Close icons
const FilterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const ProductCard = ({ product }) => {
  const images = product.images || [];
  const hasMultiple = images.length > 1;
  const [imageIndex, setImageIndex] = React.useState(0);
  const intervalRef = React.useRef(null);

  const startSlideshow = () => {
    if (!hasMultiple || intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % images.length);
    }, 1200);
  };

  const stopSlideshow = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setImageIndex(0);
  };

  React.useEffect(() => () => stopSlideshow(), []);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="rounded-2xl p-4 shadow-lg text-center w-full border border-white/50 bg-white/60 backdrop-blur supports-[backdrop-filter]:bg-white/40"
    >
      <Link to={`/product/${product._id}`} className="block">
        <div
          className="relative aspect-[4/3] overflow-hidden rounded-xl"
          onMouseEnter={startSlideshow}
          onMouseLeave={stopSlideshow}
        >
          <ImageWithFallback
            src={images[imageIndex]}
            alt={product.name}
            className="w-full h-full object-cover transition duration-500"
          />
          {hasMultiple && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, idx) => (
                <span
                  key={idx}
                  className={
                    (idx === imageIndex ? "bg-white" : "bg-white/60") +
                    " w-1.5 h-1.5 rounded-full"
                  }
                />
              ))}
            </div>
          )}
          {/* Out of Stock Badge */}
          {product.stock <= 0 && (
            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
              Out of Stock
            </div>
          )}

          <button className="absolute top-2 right-2 bg-white/80 backdrop-blur rounded-full px-3 py-1 text-xs">
            Quick View
          </button>
        </div>
        <div className="mt-3 text-center">
          <div className="text-slate-700 text-sm mb-1">{product.name}</div>
          <div className="flex items-center justify-center gap-2">
            {product.discount > 0 ? (
              <>
                <span className="text-slate-900 font-bold">₹{(product.price * (1 - product.discount / 100)).toFixed(0)}</span>
                <span className="text-slate-400 text-xs line-through">₹{product.price}</span>
                <span className="text-green-600 text-[10px] font-bold">-{product.discount}%</span>
              </>
            ) : (
              <span className="text-slate-900 font-semibold">₹{product.price}</span>
            )}
          </div>
          {product.avgRating > 0 && (
            <div className="flex items-center justify-center gap-1 mt-1">
              <Rating value={Number(product.avgRating)} readOnly size="small" precision={0.1} sx={{ color: '#fbbf24' }} />
              <span className="text-[10px] text-slate-400 font-bold">({product.reviewCount})</span>
            </div>
          )}
          {/* Stock Status */}
          {product.stock <= 0 && (
            <div className="text-red-500 text-xs mt-1">
              Currently Unavailable
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

const FilterContent = React.memo(
  ({
    sortedProductsLength,
    activeFiltersCount,
    clearAllFilters,
    searchTerm,
    onSearchChange,
    price,
    onPriceChange,
    minAvailablePrice,
    maxAvailablePrice,
    categories,
    categoriesLoading,
    categoriesError,
    selectedCategoryIds,
    onCategoryChange,
    minRating,
    onRatingChange,
    onClose,
    isOfferOnly,
    onOfferChange,
  }) => {
    return (
      <div className="bg-white rounded-2xl p-4 h-max shadow">
        <div className="font-display text-xl mb-4 flex items-center justify-between">
          <span>
            Filters
            <span className="text-slate-600 text-sm">
              {" "}
              ({sortedProductsLength} results)
            </span>
          </span>
        </div>

        {/* Clear all filters button */}
        {activeFiltersCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-black-600 text-sm mb-4 hover:underline"
          >
            Clear all filters ({activeFiltersCount})
          </button>
        )}

        {/* Search Field with controlled input */}
        <TextField
          value={searchTerm}
          onChange={onSearchChange}
          placeholder="Search products..."
          fullWidth
          size="small"
          className="mb-5"
          autoComplete="off"
          inputProps={{
            autoComplete: "off",
            spellCheck: "false",
          }}
        />
 
        {/* Special Offers Filter */}
        <div className="mb-4">
          <FormControlLabel
            control={
              <Checkbox
                checked={isOfferOnly}
                onChange={(e) => onOfferChange(e.target.checked)}
                size="small"
              />
            }
            label={<span className="text-sm font-medium">Special Offers Only</span>}
          />
        </div>

        {/* Price Range */}
        <div className="mb-6">
          <div className="text-slate-600 mt-2 mb-2">
            Price Range: ₹{price[0]} - ₹{price[1]}
          </div>
          <Slider
            value={price}
            onChange={onPriceChange}
            min={minAvailablePrice}
            max={maxAvailablePrice}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}`}
          />
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="text-slate-600 mb-2">Category</div>
          {categoriesLoading ? (
            <div className="text-sm text-slate-500">Loading categories...</div>
          ) : categoriesError ? (
            <div className="text-sm text-red-500">Error loading categories</div>
          ) : categories && categories.length > 0 ? (
            <div className="space-y-1">
              {categories.map((c) => (
                <FormControlLabel
                  key={c._id}
                  control={
                    <Checkbox
                      checked={selectedCategoryIds.has(c._id)}
                      onChange={(e) =>
                        onCategoryChange(c._id, e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={<span className="text-sm">{c.name}</span>}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No categories available
            </div>
          )}
        </div>
      </div>
    );
  }
);

const PLP = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [minAvailablePrice, setMinAvailablePrice] = React.useState(0);
  const [maxAvailablePrice, setMaxAvailablePrice] = React.useState(15000);
  const [price, setPrice] = React.useState([0, 15000]);
  const [minRating, setMinRating] = React.useState(0);
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState(() => {
    const fromQuery = searchParams.get("categoryId");
    if (fromQuery) return new Set([fromQuery]);
    if (location.state?.categoryId) return new Set([location.state.categoryId]);
    return new Set();
  });
  const [isOfferOnly, setIsOfferOnly] = React.useState(() => {
    if (searchParams.get("offer") === "1") return true;
    return location.state?.offer === true;
  });

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories();

  // Promote legacy navigation state into the URL so remounts and reloads keep the filter
  // and we do not fire a second uncategorized /products request.
  React.useEffect(() => {
    const s = location.state;
    if (!s || (!s.categoryId && s.offer !== true)) return;

    const next = new URLSearchParams(location.search);
    if (s.categoryId) next.set("categoryId", s.categoryId);
    if (s.offer === true) next.set("offer", "1");

    const qs = next.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : "" },
      { replace: true, state: null }
    );
  }, [location.state, location.pathname, location.search, navigate]);

  const skipUrlSyncRef = React.useRef(true);
  React.useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedCategoryIds.size === 1) {
          next.set("categoryId", [...selectedCategoryIds][0]);
        } else {
          next.delete("categoryId");
        }
        if (isOfferOnly) next.set("offer", "1");
        else next.delete("offer");
        return next;
      },
      { replace: true }
    );
  }, [selectedCategoryIds, isOfferOnly, setSearchParams]);

  // Pagination
  const [page, setPage] = React.useState(1);
  const [showFilters, setShowFilters] = React.useState(false);
  const pageSize = 12;

  // Memoize product query — reactive to server-filterable states
  const productQuery = React.useMemo(() => {
    const query = { limit: 50, page };

    if (selectedCategoryIds.size === 1) {
      query.categoryId = [...selectedCategoryIds][0];
    }
    if (debouncedSearchTerm) {
      query.query = debouncedSearchTerm;
    }
    if (isOfferOnly) {
      query.discounted = true;
    }

    return query;
  }, [page, selectedCategoryIds, debouncedSearchTerm, isOfferOnly]);

  // API hooks
  const { products: allProducts, loading, error, pagination } = useProducts(productQuery);

  // Set dynamic price range based on actual product prices
  React.useEffect(() => {
    if (allProducts.length > 0) {
      const prices = allProducts.map((p) => p.price).filter((p) => p != null);
      if (prices.length > 0) {
        const minP = Math.min(...prices);
        const maxP = Math.max(...prices);
        setMinAvailablePrice(minP);
        setMaxAvailablePrice(maxP);
        setPrice([minP, maxP]);
      }
    }
  }, [allProducts]);

  // Filter products based on current filter states (only price is client-side)
  const filteredProducts = React.useMemo(() => {
    if (!allProducts.length) return [];

    return allProducts.filter((product) => {
      // Price filter (not handled server-side)
      if (product.price < price[0] || product.price > price[1]) {
        return false;
      }

      // Category filter for multi-category selection (server handles single)
      if (selectedCategoryIds.size > 1) {
        const productCategoryId = product.categoryId || product.category?._id;
        if (!productCategoryId || !selectedCategoryIds.has(productCategoryId)) {
          return false;
        }
      }

      return true;
    });
  }, [allProducts, price, selectedCategoryIds]);

  // Sort filtered products
  const sortedProducts = React.useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      // Default sort by name
      return a.name.localeCompare(b.name);
    });
  }, [filteredProducts]);

  // Paginate the sorted products
  const paginatedProducts = React.useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortedProducts.slice(startIndex, startIndex + pageSize);
  }, [sortedProducts, page, pageSize]);

  // Calculate total pages
  const pageCount = Math.ceil(sortedProducts.length / pageSize);

  // Reset page to 1 when filters change
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, price, minRating, selectedCategoryIds, isOfferOnly]);

  // Calculate active filters count
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (debouncedSearchTerm) count++;
    if (price[0] > minAvailablePrice || price[1] < maxAvailablePrice) count++;
    if (minRating > 0) count++;
    if (selectedCategoryIds.size > 0) count++;
    return count;
  }, [
    debouncedSearchTerm,
    price,
    minRating,
    selectedCategoryIds,
    minAvailablePrice,
    maxAvailablePrice,
  ]);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setShowFilters(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Memoized event handlers to pass to the memoized FilterContent component
  const handleSearchChange = React.useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handlePriceChange = React.useCallback((_, newValue) => {
    if (Array.isArray(newValue)) {
      setPrice(newValue);
    }
  }, []);

  const handleCategoryChange = React.useCallback((categoryId, isChecked) => {
    setSelectedCategoryIds((prev) => {
      const newCategoryIds = new Set(prev);
      if (isChecked) {
        newCategoryIds.add(categoryId);
      } else {
        newCategoryIds.delete(categoryId);
      }
      return newCategoryIds;
    });
  }, []);

  const handleRatingChange = React.useCallback((_, newValue) => {
    setMinRating(newValue || 0);
  }, []);

  const clearAllFilters = React.useCallback(() => {
    setSearchTerm("");
    setPrice([minAvailablePrice, maxAvailablePrice]);
    setMinRating(0);
    setSelectedCategoryIds(new Set());
    setIsOfferOnly(false);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [minAvailablePrice, maxAvailablePrice, setSearchParams]);

  const handleCloseFilters = React.useCallback(() => {
    setShowFilters(false);
  }, []);

  const filterContentProps = {
    sortedProductsLength: sortedProducts.length,
    activeFiltersCount,
    clearAllFilters,
    searchTerm,
    onSearchChange: handleSearchChange,
    price,
    onPriceChange: handlePriceChange,
    minAvailablePrice,
    maxAvailablePrice,
    categories,
    categoriesLoading,
    categoriesError,
    selectedCategoryIds,
    onCategoryChange: handleCategoryChange,
    minRating,
    onRatingChange: handleRatingChange,
    onClose: handleCloseFilters,
    isOfferOnly,
    onOfferChange: setIsOfferOnly,
  };

  if (loading) return <LoadingScreen />;
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Failed to load products
          </h2>
          <p className="text-slate-600 mb-6">
            {error.response?.status === 404
              ? "No products found matching your criteria."
              : "We're having trouble loading the products. Please try again."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white gap-10 text-slate-900 min-h-screen">
      {/* Mobile Filter Toggle Button */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <Badge badgeContent={activeFiltersCount} color="primary">
          <IconButton
            onClick={() => setShowFilters(true)}
            className="bg-blue-600 text-white shadow-lg hover:bg-blue-700"
            size="large"
          >
            <FilterIcon />
          </IconButton>
        </Badge>
      </div>

      {/* Mobile Filter Overlay */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              className="w-80 max-w-[80vw] h-full bg-white overflow-y-auto pt-16"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <FilterContent {...filterContentProps} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container max-w-7xl mx-auto py-5 grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-2">
        {/* Desktop Filters */}
        <aside className="hidden lg:block sticky top-24">
          <FilterContent {...filterContentProps} />
        </aside>

        {/* Mobile Results Header */}
        <div className="lg:hidden px-4 mb-4">
          <div className="text-slate-600 text-sm">
            {sortedProducts.length} results
            {activeFiltersCount > 0 && (
              <span className="ml-2">
                ({activeFiltersCount} filter{activeFiltersCount > 1 ? "s" : ""}{" "}
                applied)
              </span>
            )}
          </div>
        </div>

        <main className="text-center px-4 lg:px-0">
          {sortedProducts.length === 0 ? (
            <div className="flex items-center justify-center min-h-[600px]">
              <div className="text-center">
                <div className="text-slate-600 text-lg mb-4">
                  No products found matching your criteria.
                </div>
                {activeFiltersCount > 0 && (
                  <button
                    style={{ backgroundColor: theme.palette.primary.main }}
                    onClick={clearAllFilters}
                    className="text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
                <AnimatePresence>
                  {paginatedProducts.map((p) => (
                    <ProductCard key={p._id} product={p} />
                  ))}
                </AnimatePresence>
              </div>

              {pageCount > 1 && (
                <div className="mt-8 flex justify-center lg:pb-8">
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, v) => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      setPage(v);
                    }}
                    color="primary"
                    shape="rounded"
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default PLP;
